<script lang="ts">
	//Stores
	import { devices, pin_unlocked_devices } from 'src/stores';
	import { os } from 'src/stores';
	//Services
	import { zwaveLightSet } from '../../../services/xz_http_reqs';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	import { format } from 'svelte-i18n';
	//Helpers
	import { get_z_wave_channel_structure_from_category } from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { hasControlRights } from 'src/helpers/user_helpers';
	//Design system components
	import { SliderV2, toast } from '@Shelly/shelly-design-system';
	//Components
	import PinUnlock from '../../PinUnlock.svelte';

	export let device, status;

	let value = 0;
	let valueCache;
	let targetValue;
	let showUnlockModal = false;
	let callbackValue;
	let isLoading = false;

	$: hasRights = hasControlRights(device);
	$: component = get_z_wave_channel_structure_from_category(device);
	$: lightStatus = status?.[component];
	$: brightness = lightStatus?.brightness ?? lightStatus?.currentValue ?? lightStatus?.value ?? 0;

	$: pinLocked =
		typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

	$: isAdjusting = targetValue !== undefined && brightness !== targetValue;

	$: if (!isAdjusting) {
		value = Number(brightness) || 0;
	}

	$: if (brightness === targetValue) {
		targetValue = undefined;
		valueCache = undefined;
	}

	const changeBrightness = async (selectedBrightness: number) => {
		if (!pinLocked) {
			valueCache = value;
			value = selectedBrightness;
			targetValue = value;
			isLoading = true;

			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}

			const res = await zwaveLightSet(device, {
				on: selectedBrightness > 0,
				brightness: selectedBrightness,
			});

			if (!res.isok) {
				value = valueCache;
				toast.open({
					type: 'danger',
					title: $format('device_control_failed_long'),
				});
			}
			isLoading = false;
		} else {
			callbackValue = selectedBrightness;
			showUnlockModal = true;
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		changeBrightness(callbackValue);
		callbackValue = undefined;
	};

	const handleClose = () => {
		value = valueCache;
	};

	const a11yProps = {
		ariaLabel: $format('label_brightness', { default: 'Set brightness' }),
		ariaLabelValue: $format('label_brightness', { default: 'Set brightness' }),
	};
</script>

<SliderV2
	id="slider-zw-brightness-{device.id}"
	class="flex-full-height min-height-0 justify-content-center"
	{a11yProps}
	title={capFirstChar($format('label_brightness', { default: 'brightness' }))}
	{value}
	disabled={!hasRights}
	on:value_change={(e) => {
		changeBrightness(e.detail.value);
	}}
	on:move={() => {
		if ($os === 'IOS') {
			hapticFeedback('selection');
		}
	}}
	{isLoading}
/>

{#if showUnlockModal}
	<PinUnlock
		bind:showModal={showUnlockModal}
		on:unlock={handlePin}
		on:close={handleClose}
		on:cancel={() => (showUnlockModal = false)}
	/>
{/if}
